import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, rupiah, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InlineAuth } from "@/components/InlineAuth";
import { toast } from "sonner";
import {
  MessageCircle, CheckCircle2, Star, ShieldCheck, MapPin, Clock, Users,
  Wallet, Minus, Plus, ArrowRight, Copy,
} from "lucide-react";

export const BookingDialog = ({ schedule, open, onClose }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ passenger_name: "", passenger_phone: "", pax: 1, pickup_address: "", dropoff_address: "", note: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [agree, setAgree] = useState(false);

  useEffect(() => {
    if (schedule) setForm((f) => ({ ...f, dropoff_address: f.dropoff_address || schedule.destination || "" }));
  }, [schedule]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, passenger_name: user.name || "", passenger_phone: user.phone || "" }));
  }, [user]);

  useEffect(() => { if (open) setAgree(false); }, [open]);

  if (!schedule) return null;

  const isCharter = schedule.service_type === "charter";
  const seatsMax = Math.max(1, schedule.seats_left || 1);
  const pax = isCharter ? 1 : Math.min(seatsMax, Math.max(1, Number(form.pax) || 1));
  const total = schedule.price * pax;
  const facilities = schedule.facilities || [];

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPax = (v) => setForm((f) => ({ ...f, pax: Math.min(seatsMax, Math.max(1, v)) }));

  const submit = async () => {
    if (!form.passenger_name.trim()) { toast.error("Isi nama penumpang"); return; }
    if (!/\d{8,}/.test(form.passenger_phone.replace(/\D/g, ""))) { toast.error("Isi nomor WhatsApp yang valid (min. 8 digit)"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", { schedule_id: schedule.id, ...form, pax });
      setResult(data);
      toast.success(`Order ${data.order.order_number} dibuat`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setResult(null);
    setForm({ passenger_name: "", passenger_phone: "", pax: 1, pickup_address: "", dropoff_address: "", note: "" });
    onClose();
  };

  const copyOrder = async () => {
    try { await navigator.clipboard.writeText(result.order.order_number); toast.success("Nomor order disalin"); } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[92vh] gap-0 overflow-y-auto p-0 sm:max-w-lg" data-testid="booking-dialog">
        {!result ? (
          <>
            {/* Trip summary header */}
            <div className="border-b border-border bg-gradient-to-b from-primary/10 to-transparent p-5">
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary/15 font-mono-terminal text-[10px] uppercase tracking-wider text-primary hover:bg-primary/15">
                    {isCharter ? "Charter Drop Off" : "Travel Door to Door"}
                  </Badge>
                  {schedule.sponsor && <Badge className="bg-amber-500/15 text-[10px] text-amber-400 hover:bg-amber-500/15">Sponsor</Badge>}
                </div>
                <DialogTitle className="mt-1 flex items-center gap-2 font-display text-2xl tracking-tight">
                  {schedule.origin} <ArrowRight className="h-5 w-5 text-primary" /> {schedule.destination}
                </DialogTitle>
              </DialogHeader>

              {/* Driver line */}
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="flex items-center gap-1 font-semibold">
                  {schedule.driver_name}
                  {schedule.driver_verified && <ShieldCheck className="h-4 w-4 text-emerald-400" />}
                </span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Star className="h-3.5 w-3.5 fill-primary text-primary" /> {schedule.driver_rating || "Baru"}
                  {schedule.driver_reviews > 0 && <span className="text-xs">({schedule.driver_reviews} ulasan)</span>}
                </span>
                {schedule.driver_priority && <span className="text-xs text-primary">· Prioritas</span>}
              </div>

              {/* Info grid */}
              <div className="mt-4 grid grid-cols-2 gap-2 font-mono-terminal text-xs sm:grid-cols-3">
                <Info icon={Clock} label="Berangkat" value={`${schedule.time} · ${schedule.date ? schedule.date.split("-").reverse().join("/") : ""}`} />
                <Info icon={Users} label={isCharter ? "Kendaraan" : "Sisa kursi"} value={isCharter ? "1 penuh" : `${schedule.seats_left} kursi`} />
                {schedule.duration && <Info icon={Clock} label="Estimasi" value={schedule.duration} />}
                {schedule.pickup_area && <Info icon={MapPin} label="Area jemput" value={schedule.pickup_area} />}
                {schedule.dropoff_area && <Info icon={MapPin} label="Area antar" value={schedule.dropoff_area} />}
              </div>

              {facilities.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="booking-facilities">
                  {facilities.map((f) => (
                    <span key={f} className="rounded-full border border-border bg-card px-2.5 py-0.5 text-[11px] text-muted-foreground">{f}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Form */}
            {!user ? (
              <div className="p-5" data-testid="booking-auth-gate">
                <div className="mb-3 flex items-start gap-2 rounded-md border border-primary/25 bg-primary/[0.06] p-3 text-xs text-muted-foreground">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>Pemesanan hanya untuk member. <b className="text-foreground">Masuk</b> atau <b className="text-foreground">daftar gratis</b> memakai nomor WhatsApp Anda untuk melanjutkan.</span>
                </div>
                <InlineAuth />
              </div>
            ) : (
            <>
            <div className="grid gap-3 p-5">
              <div className="grid gap-1.5">
                <Label>Nama Penumpang</Label>
                <Input data-testid="booking-name" value={form.passenger_name} disabled readOnly className="disabled:cursor-not-allowed disabled:opacity-70" />
              </div>

              <div className="grid gap-1.5">
                <Label>No. WhatsApp Aktif</Label>
                <Input data-testid="booking-phone" inputMode="tel" value={form.passenger_phone} disabled readOnly className="disabled:cursor-not-allowed disabled:opacity-70" />
                <span className="text-[11px] text-muted-foreground">Sesuai akun member Anda. Admin menghubungi Anda di nomor ini untuk konfirmasi.</span>
              </div>

              {!isCharter && (
                <div className="grid gap-1.5">
                  <Label>Jumlah Penumpang</Label>
                  <div className="flex items-center gap-3">
                    <button type="button" data-testid="booking-pax-dec" onClick={() => setPax(pax - 1)} disabled={pax <= 1} className="flex h-10 w-10 items-center justify-center rounded-md border border-border text-lg transition-colors hover:border-primary disabled:opacity-30">
                      <Minus className="h-4 w-4" />
                    </button>
                    <Input data-testid="booking-pax" type="number" min={1} max={seatsMax} value={pax} onChange={(e) => setPax(Number(e.target.value))} className="w-16 text-center" />
                    <button type="button" data-testid="booking-pax-inc" onClick={() => setPax(pax + 1)} disabled={pax >= seatsMax} className="flex h-10 w-10 items-center justify-center rounded-md border border-border text-lg transition-colors hover:border-primary disabled:opacity-30">
                      <Plus className="h-4 w-4" />
                    </button>
                    <span className="text-xs text-muted-foreground">maks {seatsMax} kursi</span>
                  </div>
                </div>
              )}

              <div className="grid gap-1.5">
                <Label>Alamat Penjemputan</Label>
                <Input data-testid="booking-pickup" value={form.pickup_address} onChange={set("pickup_address")} placeholder="Alamat lengkap dijemput (rumah/patokan)" />
              </div>
              <div className="grid gap-1.5">
                <Label>Alamat Tujuan</Label>
                <Input data-testid="booking-dropoff" value={form.dropoff_address} onChange={set("dropoff_address")} placeholder="Alamat lengkap tujuan" />
              </div>
              <div className="grid gap-1.5">
                <Label>Catatan (opsional)</Label>
                <Textarea data-testid="booking-note" value={form.note} onChange={set("note")} placeholder="cth. bawa 2 koper besar, jemput jam 5 pagi" />
              </div>

              {/* Price breakdown */}
              <div className="mt-1 rounded-md border border-border bg-card p-3 font-mono-terminal text-sm">
                <div className="flex items-center justify-between border-b border-border pb-2 text-muted-foreground" data-testid="booking-summary-driver">
                  <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Driver/Travel</span>
                  <span className="font-semibold text-foreground">{schedule.driver_name}</span>
                </div>
                <div className="mt-2 flex justify-between text-muted-foreground">
                  <span>{rupiah(schedule.price)} {isCharter ? "× 1 kendaraan" : `× ${pax} penumpang`}</span>
                  <span>{rupiah(total)}</span>
                </div>
                <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                  <span className="flex items-center gap-1 text-muted-foreground"><Wallet className="h-4 w-4 text-primary" /> Total</span>
                  <span className="text-xl font-bold text-primary" data-testid="booking-total">{rupiah(total)}</span>
                </div>
              </div>

              {/* Trust strip */}
              <div className="grid grid-cols-3 gap-2 text-center text-[10px] text-muted-foreground">
                <span className="flex flex-col items-center gap-1"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Dikelola Admin</span>
                <span className="flex flex-col items-center gap-1"><Wallet className="h-4 w-4 text-primary" /> Bayar setelah konfirmasi</span>
                <span className="flex flex-col items-center gap-1"><MessageCircle className="h-4 w-4 text-[#25D366]" /> Konfirmasi via WA</span>
              </div>

              {/* Estimasi & Tim Langsir ringkas */}
              <div className="rounded-md border border-primary/25 bg-primary/[0.06] p-3 text-[11px] leading-relaxed text-muted-foreground" data-testid="booking-estimate-note">
                <p className="mb-1 flex items-center gap-1.5 font-semibold text-foreground"><Clock className="h-3.5 w-3.5 text-primary" /> Jam keberangkatan bersifat estimasi</p>
                <p>Layanan <b className="text-foreground">Door to Door</b> menjemput di beberapa lokasi, sehingga waktu bisa lebih cepat/lambat dari jadwal. Bila perlu, <b className="text-foreground">Tim Langsir</b> membantu penjemputan/pengantaran — biayanya ditanggung driver, <b className="text-foreground">tanpa biaya tambahan</b> untuk Anda. Dengan memesan, Anda menyetujui ketentuan ini.</p>
                <label className="mt-2 flex cursor-pointer items-start gap-2 text-[11px] text-foreground" data-testid="booking-agree-label">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" data-testid="booking-agree-checkbox" />
                  <span>Saya memahami jam keberangkatan bersifat estimasi dan menyetujui ketentuan Door to Door &amp; Tim Langsir.</span>
                </label>
              </div>
            </div>

            <DialogFooter className="sticky bottom-0 border-t border-border bg-background p-4">
              <Button data-testid="booking-submit" onClick={submit} disabled={loading || !agree} className="w-full bg-primary py-6 text-base font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {loading ? "Memproses..." : !agree ? "Setujui ketentuan untuk melanjutkan" : <><MessageCircle className="mr-2 h-5 w-5" /> Pesan Sekarang — {rupiah(total)}</>}
              </Button>
            </DialogFooter>
            </>
            )}
          </>
        ) : (
          <div className="p-6 text-center" data-testid="booking-success">
            <CheckCircle2 className="mx-auto mb-3 h-14 w-14 text-emerald-400" />
            <h3 className="font-display text-2xl font-bold">Order Dibuat!</h3>
            <button onClick={copyOrder} className="mx-auto mt-1 flex items-center gap-1.5 font-mono-terminal text-lg text-primary hover:underline" data-testid="booking-order-number">
              {result.order.order_number} <Copy className="h-4 w-4" />
            </button>
            <p className="mt-2 text-sm text-muted-foreground">
              Lanjutkan ke WhatsApp Admin Travele untuk konfirmasi ketersediaan. Nomor driver tidak ditampilkan; seluruh pemesanan dikelola Admin.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Lacak status kapan saja di <span className="font-mono-terminal text-primary">/lacak-order</span>. Beri ulasan di <span className="font-mono-terminal text-primary">/ulasan</span> setelah perjalanan selesai.
            </p>
            <pre className="mt-4 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-card p-3 text-left text-xs text-muted-foreground">{result.wa_message}</pre>
            <a data-testid="booking-wa-link" href={result.wa_link} target="_blank" rel="noopener noreferrer">
              <Button className="mt-4 w-full bg-[#25D366] py-6 text-base font-bold text-black hover:bg-[#25D366]/90">
                <MessageCircle className="mr-2 h-5 w-5" /> Pesan via WhatsApp Admin
              </Button>
            </a>
            <Button variant="ghost" className="mt-2 w-full" onClick={close}>Tutup</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div className="rounded-md border border-border bg-card/60 p-2">
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3 w-3 text-primary" /> {label}
    </div>
    <div className="mt-0.5 truncate text-foreground" title={value}>{value}</div>
  </div>
);
